import type { CoverageItem, TrackedKeyword } from '@/client/types/media';

function stripHash(term: string) {
  return term.trim().replace(/^#/, '');
}

function isHashtagTerm(term: string) {
  return term.trim().startsWith('#');
}

/** Build a searchable blob + hashtag set from a coverage item. */
export function coverageSearchContext(item: CoverageItem) {
  const hashtags = new Set(
    (item.social?.hashtags ?? []).map((h) => stripHash(h).toLowerCase()),
  );
  // Also pull hashtags embedded in caption/body
  const textParts = [
    item.title,
    item.summary,
    item.bodyExcerpt,
    item.author,
    item.social?.caption,
    item.social?.authorHandle,
    ...(item.tags ?? []),
    ...(item.social?.mentions ?? []),
    ...(item.social?.hashtags ?? []),
  ]
    .filter(Boolean)
    .join(' ');

  const hashMatches = textParts.match(/#[\w]+/g) ?? [];
  for (const h of hashMatches) hashtags.add(stripHash(h).toLowerCase());

  return {
    text: textParts.toLowerCase(),
    hashtags,
  };
}

function termMatches(
  term: string,
  ctx: ReturnType<typeof coverageSearchContext>,
): boolean {
  const raw = term.trim();
  if (!raw) return false;

  if (isHashtagTerm(raw) || raw.startsWith('@')) {
    const needle = stripHash(raw).replace(/^@/, '').toLowerCase();
    if (isHashtagTerm(raw) && ctx.hashtags.has(needle)) return true;
    // Also allow @handles / hashtags as substrings in text
    const patterns = [
      `#${needle}`,
      `@${needle}`,
      needle,
    ];
    return patterns.some((p) => ctx.text.includes(p.toLowerCase()));
  }

  // Keyword: prefer word-ish match, fall back to includes for multi-word phrases
  const needle = raw.toLowerCase();
  if (needle.includes(' ')) return ctx.text.includes(needle);
  const re = new RegExp(`(?:^|[^a-z0-9_])${escapeRegExp(needle)}(?:[^a-z0-9_]|$)`, 'i');
  return re.test(ctx.text);
}

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Whether a coverage item matches a tracked keyword rule. */
export function itemMatchesKeyword(item: CoverageItem, keyword: TrackedKeyword): boolean {
  if (!keyword.enabled || keyword.terms.length === 0) return false;
  const ctx = coverageSearchContext(item);
  const results = keyword.terms.map((t) => termMatches(t, ctx));
  return keyword.matchMode === 'all' ? results.every(Boolean) : results.some(Boolean);
}

/** All tracked keywords that match this item. */
export function matchingKeywords(
  item: CoverageItem,
  keywords: TrackedKeyword[],
): TrackedKeyword[] {
  return keywords.filter((k) => itemMatchesKeyword(item, k));
}

export function formatKeywordTerms(keyword: TrackedKeyword): string {
  const joiner = keyword.matchMode === 'all' ? ' + ' : ' OR ';
  return keyword.terms.join(joiner);
}

export function suggestKeywordLabel(terms: string[], matchMode: TrackedKeyword['matchMode']): string {
  if (terms.length === 0) return 'Untitled';
  if (terms.length === 1) return terms[0];
  const joiner = matchMode === 'all' ? ' + ' : ' / ';
  return terms.join(joiner);
}
